import Button from 'components/ui/Button';
import copy from 'copy-to-clipboard';
import Drawer from 'components/Drawer';
import FullPageDrawer from 'compositions/FullPageDrawer';
import Position from 'components/ui/Position';
import Recipe from 'compositions/Recipe';
import Text from 'components/ui/Text';
import { FC, useCallback, useState } from 'react';
import { RecipeLinkFactory } from 'factories/recipeLinkFactory';
import { selectRoomState, selectSelectedRoomOptions } from 'store/roomSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useTranslation } from 'hooks/useTranslation';
import { useRouter } from 'next/router';
import { useGoogleTagManager } from '@grace-studio/graceful-next/hooks';
import classNames from 'classnames';
import { selectUiState, setUi } from 'store/uiSlice';
import PdfLink from 'components/PdfLink';

type RecipeProps = {
  isOpen?: boolean;
  onClose: () => void;
};

const RecipeDrawer: FC<RecipeProps> = ({ isOpen = false, onClose }) => {
  const { locale = 'en' } = useRouter();
  const gtm = useGoogleTagManager();
  const currentRoom = useAppSelector(selectRoomState);
  const currentRoomOptions = useAppSelector(selectSelectedRoomOptions);
  const translation = useTranslation();
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [sharingLink, setSharingLink] = useState('');
  const [isShowingLink, setIsShowingLink] = useState(false);
  const [isLinkInClipboard, setIsLinkInClipboard] = useState(false);

  const dispatch = useAppDispatch();
  const ui = useAppSelector(selectUiState) || {};

  const copyLink = async () => {
    gtm.trackEvent({ event: 'btn_copy_link' });
    copy(sharingLink, {
      debug: true,
      format: 'text/plain',
      onCopy: () => {
        setIsLinkInClipboard(true);
      },
    });
  };

  const createRecipeLink = useCallback(async () => {
    setIsLinkInClipboard(false);
    setIsCreatingLink(true);
    setSharingLink('');
    const recipeLink = RecipeLinkFactory.create(
      currentRoom,
      currentRoomOptions,
    );

    fetch('/api/create-recipe-link', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(recipeLink),
    })
      .then((response) => response.json())
      .then((data) => {
        setSharingLink(RecipeLinkFactory.createFullUrl(data.id, locale));
        setIsCreatingLink(false);
        setIsShowingLink(true);
      })
      .catch();
  }, [currentRoom, currentRoomOptions, locale]);

  return (
    <FullPageDrawer transition="fade" isOpen={isOpen} onClose={onClose}>
      <Recipe />
      <Position position="bottomRight" fixed className="z-10">
        <div
          className={classNames(
            'transition-all flex flex-col items-stretch gap-2 py-4',
            // ctaOpen ? 'opacity-100' : ' opacity-0 pointer-events-none',
            'opacity-100',
          )}
        >
          <Button
            style="outline"
            className={classNames(
              '!w-full transition-all ease-bounce duration-300',
              // ctaOpen ? 'scale-100 delay-100 ' : 'scale-0 translate-y-10',
              'scale-100 delay-100 ',
            )}
            disabled={isCreatingLink}
            loading={isCreatingLink}
            onClick={() => {
              createRecipeLink();
              gtm.trackEvent({ event: 'create_sharing_link', locale });
            }}
            id="create-sharing-link"
          >
            {translation('recipe.sharing.create')}
          </Button>
          <Button
            style="outline"
            className={classNames(
              '!w-full transition-all ease-bounce duration-300',
              // ctaOpen ? 'scale-100 delay-150 ' : 'scale-0 translate-y-10',
              'scale-100 delay-150 ',
            )}
            target="_blank"
            url={translation('links.products')}
            id="find-your-products"
          >
            {translation('recipe.findProducts')}
          </Button>
          <Button
            className={classNames(
              '!w-full transition-all ease-bounce duration-300',
              // ctaOpen ? 'scale-100 delay-200 ' : 'scale-0 translate-y-10',
              'scale-100 delay-200 ',
            )}
            onClick={() => {
              // gtm.trackEvent({ event: 'create_sharing_link', locale });
              dispatch(
                setUi({
                  ...ui,
                  showContactForm: true,
                }),
              );
            }}
            id="get-in-touch"
          >
            {translation('general.getInTouch')}
          </Button>
        </div>
        {/* <div className="flex flex-row justify-end">
          <Button className="self-end" onClick={() => setCtaOpen(!ctaOpen)}>
            Share
          </Button>
        </div> */}
      </Position>

      <Drawer
        isOpen={isShowingLink}
        onClose={() => setIsShowingLink(false)}
        position="center"
        transition="fade"
        className="z-[100]"
        backdropClassName="bg-black/30 z-[100]"
      >
        <div className="bg-white rounded-xl p-8">
          <Text bold size="large" elementType="div" className="mb-4">
            {translation('recipe.sharing.title')}
          </Text>

          <div className="flex">
            <div className="font-mono text-grey-500 h-10 rounded-full flex items-center justify-center px-8 mr-4 border border-blue">
              {sharingLink}
            </div>
            <Button onClick={copyLink}>
              {translation('recipe.sharing.copy')}
            </Button>
          </div>
          {isLinkInClipboard && (
            <p className="ml-8 mt-4 text-grey-500">
              {translation('recipe.sharing.copied')}
            </p>
          )}
          <div className="mt-4">
            <PdfLink recipeLink={sharingLink} />
          </div>
        </div>
      </Drawer>
    </FullPageDrawer>
  );
};

export default RecipeDrawer;
